import { VirkRegError } from "../errors/errors";

// In case of HTTP response outside of 200-299 range
async function getHTTPError(response: Response): Promise<Error> {
  const error = new VirkRegError(
    `${response.status}: ${response.statusText}`,
    response.status,
    false,
    "",
  );

  try {
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.indexOf("text/plain") !== -1) {
      const apiErrorText = await response.text();
      error.apiError = apiErrorText;
    } else {
      const apiErrorJson = await response.json();
      if (apiErrorJson) {
        error.apiError = apiErrorJson["error-code"] || "";
        error.apiErrorMessage = apiErrorJson["error-message"] || "";
      }
    }
  } catch (errorProcessingErrorContent: unknown) {
    if (errorProcessingErrorContent instanceof Error) {
      error.apiError = errorProcessingErrorContent.name;
      error.apiErrorMessage = errorProcessingErrorContent.message;
    }
  }

  return error;
}

export async function get(uri: string): Promise<Response> {
  const options: RequestInit = {
    credentials: "same-origin",
    headers: {},
  };

  const response = await fetch(uri, options);

  if (response.ok) {
    return response;
  } else {
    throw await getHTTPError(response);
  }
}

export async function getJSON<T>(uri: string): Promise<T> {
  return (await get(uri)).json();
}

export async function getPossibleUndefinedJSON<T>(
  uri: string,
): Promise<T | undefined> {
  try {
    return await getJSON<T>(uri);
  } catch (error) {
    if (error instanceof VirkRegError && error.statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

export async function request(
  method: string,
  uri: string,
  body: unknown,
  csrfToken?: string,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const headers: HeadersInit = {
    Accept: "application/vnd.digipost-v2+json",
    "Content-Type": "application/vnd.digipost-v2+json",
    "X-Requested-With": "XMLHttpRequest",
    ...extraHeaders,
  };
  if (csrfToken) {
    headers["X-CSRF-TOKEN"] = csrfToken;
  }

  const options: RequestInit = {
    method: method,
    credentials: "same-origin",
    body: typeof body === "string" ? body : JSON.stringify(body),
    headers,
  };

  const response = await fetch(uri, options);

  if (response.ok) {
    return response;
  } else {
    throw await getHTTPError(response);
  }
}

export function post(
  uri: string,
  body: unknown,
  csrfToken?: string,
): Promise<Response> {
  return request("POST", uri, body, csrfToken);
}

export function remove(
  uri: string,
  csrfToken: string,
  body: unknown = {},
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  return request("DELETE", uri, body, csrfToken, extraHeaders);
}
